/**
 * The Admin section's pages, in one place so the desktop sidebar and the
 * mobile tab strip always show the same names in the same order.
 */
export interface AdminNavItem {
  label: string;
  to: string;
}

/** Admin documents (upload, folders) moved out of the Admin list: they open from Safety Tasks. */
export const ADMIN_DOCUMENTS_PATH = "/admin/documents";

export const ADMIN_NAV_SUPER: AdminNavItem[] = [
  { label: "Catalogs", to: "/admin/catalogs" },
  { label: "Mappings", to: "/admin/mappings" },
  { label: "Templates", to: "/admin/templates" },
  { label: "Document layout", to: "/admin/layout" },
  { label: "Assignments", to: "/admin/assignments" },
  { label: "Signing Reports", to: "/admin/signing-reports" },
  { label: "Users", to: "/admin/users" },
  { label: "Subs", to: "/subcontractors" },
  { label: "Divisions", to: "/admin/divisions" },
  { label: "Recipients", to: "/admin/recipients" },
  { label: "Assets", to: "/admin/assets" },
  { label: "Inspections", to: "/admin/inspections" },
  { label: "Credential", to: "/admin/certifications" },
  { label: "Job Titles", to: "/admin/job-titles" },
  { label: "Permits", to: "/admin/permits" },
  { label: "Invites", to: "/admin/invitations" },
  { label: "Actions", to: "/admin/corrective-actions" },
  { label: "Export", to: "/admin/export" },
  { label: "Backup & Server Status", to: "/admin/backup" },
  { label: "App Update", to: "/admin/app-update" },
  { label: "Reports", to: "/reports" },
  { label: "Audit Log", to: "/admin/audit-log" },
  { label: "Birthdays", to: "/admin/birthdays" },
  { label: "Messages", to: "/admin/notes" },
];

export const ADMIN_NAV_DIV: AdminNavItem[] = [
  { label: "Assignments", to: "/admin/assignments" },
  { label: "Signing Reports", to: "/admin/signing-reports" },
  { label: "Users", to: "/admin/users" },
  { label: "Subs", to: "/subcontractors" },
  { label: "Recipients", to: "/admin/recipients" },
  { label: "Job Titles", to: "/admin/job-titles" },
  { label: "Credential", to: "/admin/certifications" },
  { label: "Actions", to: "/admin/corrective-actions" },
  { label: "Messages", to: "/admin/notes" },
];

/**
 * Pages about how the app itself is set up. They sit together in one folder
 * ("App setup") in the Admin section so the day-to-day pages stay uncluttered.
 */
export const APP_SETUP_PATHS = [
  "/admin/catalogs",
  "/admin/mappings",
  "/admin/templates",
  "/admin/layout",
  "/admin/app-update",
];
export const APP_SETUP_KEY = "group:app-setup";
export const APP_SETUP_LABEL = "App setup";

export type AdminNavEntry =
  | { kind: "item"; key: string; item: AdminNavItem }
  | { kind: "group"; key: string; label: string; items: AdminNavItem[] };

/** An admin's own order: top-level keys, plus the order inside the App setup folder. */
export interface AdminNavOrder {
  top: string[];
  setup: string[];
}

function applyOrder<T>(items: T[], keyOf: (t: T) => string, saved: string[] | undefined): T[] {
  if (!saved || saved.length === 0) return items;
  const rank = new Map(saved.map((k, i) => [k, i]));
  // Pages the admin has not ordered yet (new ones) keep their default place after the ordered ones.
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const ra = rank.get(keyOf(a.it));
      const rb = rank.get(keyOf(b.it));
      if (ra !== undefined && rb !== undefined) return ra - rb;
      if (ra !== undefined) return -1;
      if (rb !== undefined) return 1;
      return a.i - b.i;
    })
    .map((x) => x.it);
}

/** Group the setup pages into one folder (when there are 2+) and apply the admin's saved order. */
export function buildAdminNav(items: AdminNavItem[], saved?: AdminNavOrder | null): AdminNavEntry[] {
  const setupItems = items.filter((i) => APP_SETUP_PATHS.includes(i.to));
  const grouped = setupItems.length >= 2;
  const entries: AdminNavEntry[] = [];
  let groupAdded = false;
  for (const item of items) {
    if (grouped && APP_SETUP_PATHS.includes(item.to)) {
      if (!groupAdded) {
        entries.push({
          kind: "group",
          key: APP_SETUP_KEY,
          label: APP_SETUP_LABEL,
          items: applyOrder(setupItems, (i) => i.to, saved?.setup),
        });
        groupAdded = true;
      }
    } else {
      entries.push({ kind: "item", key: item.to, item });
    }
  }
  return applyOrder(entries, (e) => e.key, saved?.top);
}

/** First page an admin lands on in the Admin section. */
export function adminHome(role?: string | null): string {
  return role === "admin" ? "/admin/catalogs" : "/admin/assignments";
}
